import { type ThemeAppearance } from '@atlaskit/lozenge/lozenge';

import { MAX_STATUS_LENGTH } from '../../../../../../../common/ui/status-lozenge-button';

/**
 * Maximum number of characters of the freeform project `state` that get rendered
 * inline in the lozenge before truncating with an ellipsis (the full value is
 * still surfaced via the lozenge button's popup).
 *
 * Re-exported from {@link MAX_STATUS_LENGTH} — the single source of truth used by
 * `StatusLozengeButton`, which performs the actual truncation — so the threshold
 * asserted in tests can never silently diverge from what the component applies.
 */
export const PROJECT_STATE_MAX_LENGTH: number = MAX_STATUS_LENGTH;

/**
 * Lozenge appearance used when a project's freeform state does not match any of
 * the known keyword buckets below.
 */
export const DEFAULT_PROJECT_STATE_APPEARANCE: ThemeAppearance = 'default';

/**
 * Maps a project's freeform `state`/`status` string to a semantic
 * {@link ThemeAppearance} so the lozenge is colour-coded (mirroring the way
 * `SearchResultAtlasProject` colours its status lozenge) instead of always
 * rendering grey.
 *
 * Because ServiceNow project/demand states are freeform strings (e.g. `Draft`,
 * `In Progress`, `Work in Progress`, `Closed Complete`, `Cancelled`), we match
 * on lowercased keywords rather than an exhaustive enum. Anything unrecognised
 * falls back to the neutral `default` (grey) appearance.
 *
 * Appearance → colour reference (`@atlaskit/lozenge`):
 * - `success`    → green  (completed / approved)
 * - `inprogress` → blue   (active / work in progress)
 * - `new`        → purple (newly created / open / draft-pending review)
 * - `moved`      → yellow (on hold / pending / waiting)
 * - `removed`    → red    (cancelled / rejected / closed incomplete)
 * - `default`    → grey   (everything else, incl. plain `Draft`)
 */
export const getProjectStateAppearance = (state?: string | null): ThemeAppearance => {
	const normalised = state?.trim().toLowerCase();
	if (!normalised) {
		return DEFAULT_PROJECT_STATE_APPEARANCE;
	}

	// Order matters: more specific / negative phrases are checked before the
	// generic positive keywords they may contain (e.g. "closed incomplete"
	// before "complete", "not approved" before "approved").
	if (
		/\b(cancel(?:led|ed)?|reject(?:ed)?|closed incomplete|closed skipped|abandoned|withdrawn|failed)\b/.test(
			normalised,
		)
	) {
		return 'removed';
	}

	if (/\b(on hold|hold|pending|awaiting|blocked|deferred|waiting|paused)\b/.test(normalised)) {
		return 'moved';
	}

	if (
		/\b(complete(?:d)?|closed complete|done|approved|delivered|finished|resolved)\b/.test(
			normalised,
		)
	) {
		return 'success';
	}

	if (
		/\b(in progress|in-progress|wip|work in progress|active|started|ongoing|implementing|executing)\b/.test(
			normalised,
		)
	) {
		return 'inprogress';
	}

	if (/\b(open|new|requested|submitted|scoping|review|assess(?:ment)?)\b/.test(normalised)) {
		return 'new';
	}

	return DEFAULT_PROJECT_STATE_APPEARANCE;
};

/**
 * Normalises a project's freeform `state`/`status` string for display in a
 * lozenge: trims surrounding whitespace and capitalises the first letter.
 *
 * Truncation is intentionally NOT applied here — it is the responsibility of the
 * presentational lozenge component (see {@link PROJECT_STATE_MAX_LENGTH}) so the
 * full, capitalised value remains available for tooltips/popups.
 *
 * Returns `null` when the input is empty so callers can skip rendering the
 * state lozenge.
 */
export const formatProjectState = (state?: string | null): string | null => {
	const trimmed = state?.trim();
	if (!trimmed) {
		return null;
	}

	return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};
